import type { CSSObject, Theme } from "@mui/material/styles";
import { useBannerCountdown } from "../runtime/useBannerCountdown";
import { useDashboardBanner } from "../runtime/useDashboardBanner";
import { TopBarBanner } from "../templates/TopBarBanner";
import { countdownContent } from "./countdownContent";
import { iconVisualOf } from "./bannerIcons";
import { PageFrame } from "./PageFrame";

/**
 * The Top bar banner the runtime picks for this page, aligned with the page
 * content container as `PromoBanner` is. A sticky one stays under the app bar,
 * gap included, while the page scrolls beneath it.
 */
export const TopBarPlacement = () => {
  const { banner, followCta, close } = useDashboardBanner({
    template: "topBar",
  });
  const countdown = useBannerCountdown(banner);

  if (!banner) return null;

  const content = countdownContent(countdown, {
    title: banner.text,
    text: null,
    ctaLabel: banner.cta.label,
  });

  return (
    <PageFrame
      data-testid="top-bar-placement"
      pt={3}
      sx={banner.position === "sticky" ? stickyUnderAppBar : undefined}
    >
      <TopBarBanner
        text={content.title}
        code={(content.offerOn && banner.code) || undefined}
        countdown={content.countdown}
        ctaLabel={content.ctaLabel}
        onCtaClick={() => followCta(banner)}
        onClose={banner.closePeriod ? () => close(banner) : undefined}
        variant={banner.style}
        visual={iconVisualOf(banner.icon)}
      />
    </PageFrame>
  );
};

/** Sticks at the app bar's height, which MUI's toolbar mixin sets per breakpoint. */
const stickyUnderAppBar = (theme: Theme): CSSObject => {
  const { minHeight, ...queries } = theme.mixins.toolbar;
  const topPerQuery = Object.entries(queries).map(([query, style]) => [
    query,
    { top: (style as { minHeight: number }).minHeight },
  ]);
  return {
    position: "sticky",
    top: minHeight,
    ...Object.fromEntries(topPerQuery),
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.background.default,
  };
};
